import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomInput from "../../Subcomponents/CustomInput";
import { supabase } from "../../supabaseClient";
import { errorTost } from "../../Services/Toast";
import { set } from "lodash";

const IdRecoveryModal = ({ open, onClose }) => {
  const [panValue, setPanValue] = useState("");
  const [recoveredId, setRecoveredId] = useState("");
  const [cpStatus, setCpStatus] = useState("");
  const [copied, setCopied] = useState(false);
  const [cpInfo, setCpInfo] = useState(null);
  const [fosInfo, setFosInfo] = useState(null);

  const handleIdRecovery = async () => {
    try {
      const { data: kycData, error: kycError } = await supabase
        .from("KycDocuments")
        .select("cpId, fosId")
        .eq("docNumber", panValue)
        .eq("docType", "PanCard")
        .limit(1)
        .single();

      if (kycError || !kycData) {
        errorTost("No record found for this PAN.");
        return;
      }

      const { cpId, fosId } = kycData;

      if (cpId) {
        const { data: cpData, error: cpErr } = await supabase
          .from("CP")
          .select("cpDisplayId, approvalStatus")
          .eq("cpId", cpId)
          .limit(1)
          .single();

        if (cpErr || !cpData?.cpDisplayId) {
          errorTost("CPID not found in CP table.");
          return;
        }

        const { data: cpContactData, error: cpContactError } = await supabase
          .from("CpContactPerson")
          .select("name, email, contactNumber")
          .eq("cpId", cpId)
          .limit(1)
          .single();

        if (cpContactError || !cpContactData) {
          errorTost("Contact information for CP not found.");
          return;
        }

        setCpInfo(cpContactData); // Set CP contact information
        setRecoveredId(`CPID${cpData.cpDisplayId}`);
        setCpStatus(cpData.approvalStatus);
        return;
      }

      if (fosId) {
        const { data: fosData, error: fosErr } = await supabase
          .from("FOS")
          .select("fosDisplayId")
          .eq("fosId", fosId)
          .limit(1)
          .single();

        if (fosErr || !fosData?.fosDisplayId) {
          errorTost("FOSID not found in FOS table.");
          return;
        }

        const { data: fosContactData, error: fosContactError } = await supabase
          .from("FOS")
          .select("name, email, contactNumber, approvalStatus")
          .eq("fosId", fosId)
          .limit(1)
          .single();

        if (fosContactError || !fosContactData) {
          errorTost("Contact information for FOS not found.");
          return;
        }

        setFosInfo(fosContactData); // Set FOS contact information
        setRecoveredId(`FOSID${fosData.fosDisplayId}`);
        return;
      }

      errorTost("CPID or FOSID not found for this PAN.");
    } catch (err) {
      errorTost("Something went wrong while searching your ID.");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(recoveredId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleClose = () => {
    setPanValue("");
    setRecoveredId("");
    setCpStatus("");
    setCpInfo(null);
    setFosInfo(null);
    setCopied(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          width: 300,
          margin: "10% auto",
          padding: 3,
          bgcolor: "white",
          borderRadius: 2,
          position: "relative",
          animation: "fadeIn 0.3s ease-in-out"
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Input */}
        <CustomInput
          label="Enter PAN Number"
          value={panValue}
          handleChange={(val) => setPanValue(val.trim().toUpperCase())}
          placeholder="ABCDE1234F"
          mandatory
        />

        {/* Search Button */}
        <Button
          fullWidth
          variant="contained"
          sx={{ mt: 2 }}
          onClick={handleIdRecovery}
          disabled={!panValue}
        >
          Search
        </Button>

        {/* Recovered ID Display */}
        {recoveredId && (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              animation: "fadeInUp 0.5s ease-in-out"
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", fontSize: "2rem" }}
            >
              {recoveredId}
            </Typography>

            {/* Copy Button */}
            <Tooltip title={copied ? "Copied!" : "Copy"}>
              <IconButton
                onClick={handleCopy}
                color={copied ? "success" : "primary"}
                size="small"
              >
                {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* CP Information Display */}
        {cpInfo && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.3rem" }}><strong>Status:</strong> {cpStatus}</Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.3rem" }}><strong>Email:</strong> {cpInfo.email}</Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.3rem" }}><strong>Contact Number:</strong> {cpInfo.contactNumber}</Typography>
          </Box>
        )}

        {/* FOS Information Display */}
        {fosInfo && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.3rem" }}><strong>Status:</strong> {fosInfo.approvalStatus}</Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.3rem" }}><strong>Email:</strong> {fosInfo.email}</Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "1.3rem" }}><strong>Contact Number:</strong> {fosInfo.contactNumber}</Typography>
          </Box>
        )}

        {/* Animations */}
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; transform: scale(0.95); }
              to { opacity: 1; transform: scale(1); }
            }
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}
        </style>
      </Box>
    </Modal>
  );
};

export default IdRecoveryModal;